import React from 'react';
import Head from 'next/head';
import { ThemeProvider } from 'react-jss';
import { LANGUAGES_BY_CODE, LANGUAGES_BY_ID, getLanguageTagById } from '@sm/locale';
import { defineMessages, t } from '@sm/intl';
import { WeightedElementSelector } from '~app/pages/SurveyThanks/utils';
import config from '~helpers/config';
import routeHandler from '~helpers/routeHandler';
import { buildSmOptions } from '~helpers/buildSmOptions';
import surveyThanks, { SurveyThanksServerSideData } from '~helpers/pages/survey-thanks';
import { CustomGetServerSideProps } from '~helpers/middleware/types';
import fetchLocaleMessages from '~helpers/fetchLocaleMessages';
import withExperiments, { findActiveTreatment, getPollingExperiment } from '~helpers/experiments';
import { ExperimentData, PollingExperimentData } from '~helpers/types';
import useEndPageView from '~app/hooks/useEndPageView';
import PageLayout from '~components/pageLayout';

import {
  GUAC_EXISTING_USER_PAGES,
  US_GEO_PAGES,
  GAC_GEO_PAGES,
  NON_GUAC_GEO_PAGES,
  NON_GUAC_EXISTING_USER_PAGES,
  NON_ENGLISH_PAGES,
  endpageVariants,
} from '~app/pages/SurveyThanks/constants';

import theme, { useGlobalStyles } from '~app/styles/cmsTheme';
import { EndpageVariant, SmParams, PageWithLayout } from '~helpers/pages/types';
import OneTrustFooterScript from '~components/oneTrustScript';
import { CollectorType } from '~lib/generatedGqlTypes';
import { ENDPAGE_122, ENDPAGE_132, setExperimentCookies } from '~app/pages/experiments';
import getPollingTemplate from '~helpers/pages/survey-thanks/polling';

const COPY = defineMessages({
  DEFAULT_THANKS_MESSAGE: {
    id: 'survey-thanks.thanksMessage',
    defaultMessage: 'Thank you for taking this survey.',
  },
  THANKS_PAGE_TITLE: {
    id: 'survey-thanks.title',
    defaultMessage: 'Thank you! Create Your Own Online Survey Now! | SurveyMonkey',
  },
  RESEARCH_PAGE_TITLE: {
    id: 'survey-thanks.research-title',
    defaultMessage: 'Research.net - Powerful tool for creating web surveys.  Online survey software made easy!',
  },
  NON_BRANDED_TITLE: {
    id: 'survey-thanks.non-branded-title',
    defaultMessage: 'Online Survey Software: Survey Thanks',
  },
});

const fallbackCollectorData = {
  collector: {
    id: 'null',
    type: 'WEBLINK' as CollectorType,
    hideBranding: true,
  },
};

type ServerSideProps = SurveyThanksServerSideData & {
  showFooter: boolean;
  canonical: string;
  canonicalHostname: string;
  endpageVariant: EndpageVariant;
  smParams?: SmParams;
  countryCode: string;
  countryRegion: string;
  experiments?: ExperimentData[];
};

export const getServerSideProps: CustomGetServerSideProps<ServerSideProps> = async ({
  req,
  res,
  query,
  resolvedUrl,
}) => {
  // If source is a kiosk collector then assign a sm param to prevent error
  const { brandedWeblinkSmParam } = config;
  const expectedQuery = !query?.sm ? { ...query, sm: brandedWeblinkSmParam } : query;

  const handler = routeHandler({
    query: expectedQuery,
    pageDataLoader: surveyThanks,
    owners: '@webplatform/surveys',
    onetrustBannerEnabled: true,
  });

  const smParams = buildSmOptions(expectedQuery)?.smParams;

  withExperiments([ENDPAGE_132.identifier, ENDPAGE_122.identifier], {
    context: { survey_language: getLanguageTagById(smParams?.languageId ?? '')?.code },
  })(handler);

  await handler.run(req, res);

  const {
    epTemplate = '',
    isBasicEndpageGroup,
    isNonBrandedSmParam = false,
    isEmbeddedSmParam = false,
    isWhiteLabel = false,
    isGovernment = false,
    randomNumber = 0,
    languageId: surveyLangId = '',
    isExistingUser,
    canonical = '',
    canonicalHostname = '',
  } = req.pageProps?.pageOptions || {};
  let experiments = req?.pageProps?.experiments ?? [];

  const collectorData = req.pageProps?.collectorData || fallbackCollectorData;
  const fetchServerSideDataSuccess = req.pageProps?.fetchServerSideDataSuccess || false;
  const { type: collectorType } = collectorData.collector ?? {};
  const { countryRegion, countryCode, slLanguageLocale, languageCode } = req?.smenviron;
  const translationData = await fetchLocaleMessages(slLanguageLocale, languageCode);

  // the cast is safe. `parseInt` knows how to handle non-string values and NaN will trigger the fallback
  const parsedLanguageId = parseInt(surveyLangId as string, 10) || LANGUAGES_BY_CODE.en.id;
  const isNonEnglish = parsedLanguageId !== LANGUAGES_BY_CODE.en.id;
  const isNonBranded = isNonBrandedSmParam === true || collectorData.collector?.hideBranding;
  const isEmbedded = isEmbeddedSmParam || collectorType?.toUpperCase() === 'POPUP';
  const showFooter = !isNonBranded && !isEmbedded;
  const isUSA = countryCode === 'US';
  const isGAC = countryCode === 'GB' || countryCode === 'AU' || countryCode === 'CA';

  const pollingExperiment: PollingExperimentData = await getPollingExperiment(req, {
    survey_language: LANGUAGES_BY_ID[parsedLanguageId]?.code ?? LANGUAGES_BY_CODE.en.code,
    group_id: smParams?.groupId,
    collector_id: parseInt(smParams?.collectorId as string, 10) || null,
    user_id: smParams?.userId,
  });
  const isPollingPage = !!pollingExperiment?.pollName;

  // The array should only have experiment identifiers which are in the endpage bucket
  const activeExperiment = findActiveTreatment([ENDPAGE_132.identifier, ENDPAGE_122.identifier], experiments);
  const experimentTemplate: EndpageVariant = activeExperiment.split('-')?.[2] as EndpageVariant;

  let endpageVariant: EndpageVariant;
  switch (true) {
    case !!epTemplate:
      endpageVariant = epTemplate;
      break;
    case isEmbedded:
      endpageVariant = isNonBranded ? 'embeddedNonBranded' : 'embedded';
      break;
    case isGovernment:
      endpageVariant = 'nonBranded';
      break;
    case isWhiteLabel:
      endpageVariant = 'research';
      break;
    case isNonBranded:
      endpageVariant = 'nonBranded';
      break;
    case isBasicEndpageGroup:
      endpageVariant = 'rebrandEpForm';
      break;
    case isPollingPage:
      // add the polling experiment to the beginning of the experiment so its cookies get set
      experiments = [pollingExperiment, ...experiments];
      endpageVariant = getPollingTemplate(pollingExperiment);
      break;
    case !!experimentTemplate:
      endpageVariant = experimentTemplate;
      break;
    case isNonEnglish:
      endpageVariant = WeightedElementSelector(NON_ENGLISH_PAGES, randomNumber).page;
      break;
    case isExistingUser && (isUSA || isGAC):
      endpageVariant = WeightedElementSelector(GUAC_EXISTING_USER_PAGES, randomNumber).page;
      break;
    case isUSA:
      endpageVariant = WeightedElementSelector(US_GEO_PAGES, randomNumber).page;
      break;
    case isGAC:
      endpageVariant = WeightedElementSelector(GAC_GEO_PAGES, randomNumber).page;
      break;
    case isExistingUser:
      endpageVariant = WeightedElementSelector(NON_GUAC_EXISTING_USER_PAGES, randomNumber).page;
      break;
    default:
      endpageVariant = WeightedElementSelector(NON_GUAC_GEO_PAGES, randomNumber).page;
      break;
  }

  // TODO: Retire the usage of StaticContext and use page props instead.
  // Additional page-specific (reCAPTCHA) data needed in StaticContext
  req.addClientPayload('staticData', {
    recaptchaV3: {
      enabled: process?.env?.SECURITY_RECAPTCHA_V3_ENABLED?.toLowerCase() === 'true' ?? false,
      siteKey: process?.env?.SECURITY_RECAPTCHA_V3_SITE_KEY ?? '',
      url: process?.env?.SECURITY_RECAPTCHA_V3_URL ?? '',
      urlChina: process?.env?.SECURITY_RECAPTCHA_V3_URL_CHINA ?? '',
      useEnterprise: process?.env?.SECURITY_RECAPTCHA_V3_USE_ENTERPRISE?.toLowerCase() === 'true' ?? false,
    },
    smParams,
    templateType: endpageVariant ?? null,
    pollingSurveyId: pollingExperiment?.surveyId ?? '',
    cParam: req?.pageProps?.cParam,
  });

  return {
    props: {
      // added to the request via routeHandler
      ...req.payloads,
      // added to the request via the pageDataLoaderFactory
      ...req.pageProps,
      fetchServerSideDataSuccess,
      collectorData,
      translationData,
      layout: {
        variant: 'SurveyMonkey',
        options: {
          pageId: 'SurveyThanks',
          legacyWeb: 'anonweb',
          includeHeader: false,
          includeFooter: showFooter,
          includeGTM: true,
        },
      },
      showFooter,
      canonical,
      canonicalHostname,
      endpageVariant,
      smParams,
      countryCode,
      countryRegion,
      experiments,
    },
  };

  // SurveyThanks shows the basic branded page even if an error occurs
  // if (!pageProps.props.fetchServerSideDataSuccess) {
  //   return false;
  // }
  // @todo: As commented as above, then the return value should not be a boolean.
  // Instead, we should either rewrite on the middleware level or handle it in
  // the rendering component.
};

const SurveyThanks: PageWithLayout<ServerSideProps> = ({
  endpageVariant,
  smParams,
  countryCode,
  countryRegion,
  experiments,
  canonicalHostname,
}) => {
  setExperimentCookies(experiments);

  useGlobalStyles();

  const Endpage = (): React.ReactElement => endpageVariants[endpageVariant];
  const titleVariants: { [key: string]: string } = {
    embeddedNonBranded: t(COPY.NON_BRANDED_TITLE),
    nonBranded: t(COPY.NON_BRANDED_TITLE),
    research: t(COPY.RESEARCH_PAGE_TITLE),
  };

  useEndPageView({
    countryCode,
    countryRegion,
    pageName: 'survey thanks',
    templateType: endpageVariant,
    surveyId: smParams?.surveyId,
    collectorId: smParams?.collectorId,
    respondentId: smParams?.respondentId,
    languageId: smParams?.languageId,
  });

  return (
    <ThemeProvider theme={theme}>
      <Head>
        <base target="_top" href="/" />
        <link rel="canonical" href={`${canonicalHostname}/survey-thanks/`} />
        <title>{titleVariants[endpageVariant] ?? t(COPY.THANKS_PAGE_TITLE)}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Endpage />
      <OneTrustFooterScript />
    </ThemeProvider>
  );
};

SurveyThanks.getLayout = function getLayout(page) {
  const { layout, staticData, translationData } = page.props;

  return (
    <PageLayout layout={layout} staticData={staticData} translationData={translationData}>
      {page}
    </PageLayout>
  );
};

export default SurveyThanks;
